import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  House,
  CircleUserRound,
  Handshake,
  Building2,
  FileQuestion,
  ArrowRight,
  Check,
  Shield,
  Zap,
  Globe2,
  Star,
  Download,
  UserCheck,
  Send,
  X,
  Lock,
  FileCheck,
  Server,
  Clock,
  DollarSign,
  Smartphone,
  FileText,
} from 'lucide-react';

import Header from '../components/common/landingPage/Header';
import Footer from '../components/common/landingPage/Footer';
import FaqItem from '../components/common/landingPage/FaqItem';
import WhatsApp from '../components/common/landingPage/WhatsApp';

import NeutronIcon from '../assets/images/landing-page/neutron.png';
import BridgeIcon from '../assets/images/landing-page/bridge.png';
import AwsIcon from '../assets/images/landing-page/aws.png';
import FireblocksIcon from '../assets/images/landing-page/fireblocks.png';

import GooglePlayLogo from '../assets/images/landing-page/googlePlayLogo.svg';
import AppStoreLogo from '../assets/images/landing-page/appStoreLogo.svg';

import ColombiaFlag from '../assets/images/country/colombia.png';
import MexicoFlag from '../assets/images/country/mexico.png';
import UsFlag from '../assets/images/country/us.png';
import EuropeFlag from '../assets/images/country/europe.png';
import BrazilFlag from '../assets/images/country/brazil.png';

import Video1 from '../assets/videos/1.mp4';
import HeroVideo from '../assets/videos/hero.mp4';
import Video2 from '../assets/videos/2.mp4';
import Video3 from '../assets/videos/3.mp4';
import Video4 from '../assets/videos/4.mp4';
import Video5 from '../assets/videos/5.mp4';
import ThumbImage1 from '../assets/videos/1.jpg';
import ThumbImage2 from '../assets/videos/2.jpg';
import ThumbImage3 from '../assets/videos/3.jpg';
import ThumbImage4 from '../assets/videos/4.jpg';

import Avatar1 from '../assets/images/avatars/1.jpg';
import Avatar2 from '../assets/images/avatars/2.jpg';
import Avatar3 from '../assets/images/avatars/3.jpg';

const Partners = [
  { image: BridgeIcon, name: 'Bridge' },
  { image: FireblocksIcon, name: 'Fireblocks' },
  { image: AwsIcon, name: 'AWS' },
  { image: NeutronIcon, name: 'Neutron' },
];

const Apps = [
  {
    icon: GooglePlayLogo,
    description: 'Disponible en',
    platform: 'Google Play',
    url: 'https://play.google.com/store/apps/details?id=com.flowexpay.app',
  },
  {
    icon: AppStoreLogo,
    description: 'Descárgala en la',
    platform: 'App Store',
    url: 'https://apps.apple.com/app/flowexpay/id6762288494',
  },
];

const VirtualAccounts = [
  { flag: UsFlag, country: 'Estados Unidos', currency: 'USD', video: Video4, thumb: ThumbImage4 },
  { flag: EuropeFlag, country: 'Europa', currency: 'EUR', video: Video3, thumb: ThumbImage3 },
  { flag: MexicoFlag, country: 'México', currency: 'MXN', video: Video2, thumb: ThumbImage2 },
  { flag: BrazilFlag, country: 'Brasil', currency: 'BRL (PIX)', video: Video1, thumb: ThumbImage1 },
  { flag: ColombiaFlag, country: 'Colombia', currency: 'COP', video: Video1, thumb: ThumbImage1 },
];

const Reviews = [
  {
    text: 'Antes tardaba días en enviarle dinero a mi familia en Perú. Ahora con FlowexPay lo hacen al instante, y con menos comisiones.',
    avatar: Avatar1,
    username: 'Camila R.',
    location: 'Bogotá, Colombia',
    rating: 5,
  },
  {
    text: 'Cobro en dólares y gracias a FlowexPay puedo transferir el dinero a mi cuenta local sin complicaciones. Todo es rápido y transparente.',
    avatar: Avatar2,
    username: 'Lucas T.',
    location: 'Medellín, Colombia',
    rating: 5,
  },
  {
    text: 'Uso FlowexPay para enviarle plata a mis hijos que estudian afuera. Es seguro, práctico y sé que la reciben en segundos.',
    avatar: Avatar3,
    username: 'Marta S.',
    location: 'Cali, Colombia',
    rating: 5,
  },
];

const Faqs = [
  {
    question: '¿Qué es FlowexPay y qué servicios ofrece?',
    answer:
      'FlowexPay es una plataforma colombiana creada por Loaded Colombia SAS que hace que enviar dinero al mundo sea fácil, rápido y seguro. Ayudamos a personas y empresas a enviar dinero desde Colombia a países como Estados Unidos, Europa (España, Francia, Alemania), México, Brasil y Canadá, y a tener cuentas virtuales para pagar o recibir dinero como si estuvieran allá.',
  },
  {
    question: '¿Por qué elegir FlowexPay?',
    answer:
      'Somos más rápidos y económicos que los bancos, ofrecemos cuentas virtuales en EE. UU., Europa, México y Brasil, y usamos socios seguros como Amazon Web Services, Fireblocks, Bridge y Twilio. También puedes ganar recompensas si eliges prestar tus dólares digitales.',
  },
  {
    question: '¿Cómo empiezo a usar FlowexPay?',
    answer:
      'Regístrate en flowexpay.com o descarga nuestra app, verifica tu identidad y recarga tu cuenta con pesos colombianos vía PSE o desde bancos en EE. UU., Europa o México. En menos de 5 minutos estás listo para enviar tu primera transferencia.',
  },
  {
    question: '¿Cuánto cuesta enviar dinero con FlowexPay?',
    answer:
      'Nuestra comisión es del 0,5% de entrada y 0,5% de salida (1% total por operación), completamente transparente y sin comisiones ocultas. Esto es hasta 8 veces más barato que un banco tradicional, donde las comisiones suelen estar entre el 5% y el 8% del monto enviado. Puedes ver el costo exacto antes de confirmar cada transferencia.',
  },
  {
    question: '¿Es seguro usar FlowexPay?',
    answer:
      'Sí. Estamos respaldados por socios regulados a nivel institucional: Bridge (custodia y cumplimiento), Fireblocks (seguridad de activos), AWS (infraestructura), y cumplimos con las regulaciones UIAF/KYC colombianas. Tu dinero y tus datos están protegidos con estándares bancarios.',
  },
];

const Features = [
  {
    icon: Zap,
    title: 'Transferencias en minutos',
    description: 'Envía dinero a más de 50 países y llega al instante, no en días.',
  },
  {
    icon: Globe2,
    title: 'Cuentas virtuales globales',
    description: 'Recibe pagos en USD, EUR, MXN o BRL como si tuvieras cuenta local.',
  },
  {
    icon: Shield,
    title: 'Seguridad institucional',
    description: 'Respaldados por Bridge, Fireblocks y AWS. Cumplimos UIAF/KYC.',
  },
];

// Simple IntersectionObserver hook for scroll-reveal
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
}

// Interactive currency calculator
function CurrencyCalculator() {
  const [cop, setCop] = useState(1000000);
  const rate = 0.000243; // 1 COP ≈ 0.000243 USD (example; replace with live rate)
  const feeIn = 0.005; // 0.5% entrada
  const feeOut = 0.005; // 0.5% salida
  const copAfterIn = cop * (1 - feeIn);
  const usdGross = copAfterIn * rate;
  const usd = (usdGross * (1 - feeOut)).toFixed(2);
  const totalFeeCop = Math.round(cop * (feeIn + feeOut - feeIn * feeOut));
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 md:p-10 max-w-xl mx-auto">
      <p className="text-sm font-medium text-gray-500 mb-2">Tú envías</p>
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
        <input
          type="number"
          value={cop}
          onChange={(e) => setCop(Math.max(0, Number(e.target.value) || 0))}
          className="text-3xl md:text-4xl font-bold text-[#0A0D12] bg-transparent outline-none w-full"
        />
        <div className="flex items-center gap-2 shrink-0 bg-gray-50 px-4 py-2 rounded-full">
          <img src={ColombiaFlag} alt="COP" className="w-6 h-6 rounded-full object-cover" />
          <span className="font-semibold text-gray-800">COP</span>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-500 mb-2">Ellos reciben</p>
      <div className="flex items-center justify-between">
        <p className="text-3xl md:text-4xl font-bold text-[#092F7B]">${usd}</p>
        <div className="flex items-center gap-2 shrink-0 bg-gray-50 px-4 py-2 rounded-full">
          <img src={UsFlag} alt="USD" className="w-6 h-6 rounded-full object-cover" />
          <span className="font-semibold text-gray-800">USD</span>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-100 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Tasa de cambio</span>
          <span className="font-medium text-gray-700">
            1 USD ≈ {Math.round(1 / rate).toLocaleString()} COP
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Comisión entrada (0,5%)</span>
          <span className="font-medium text-gray-700">
            {Math.round(cop * feeIn).toLocaleString()} COP
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Comisión salida (0,5%)</span>
          <span className="font-medium text-gray-700">
            ≈ {Math.round(copAfterIn * rate * feeOut * (1 / rate)).toLocaleString()} COP
          </span>
        </div>
        <div className="flex justify-between text-sm pt-2 mt-2 border-t border-gray-100">
          <span className="text-[#092F7B] font-semibold">Total comisión</span>
          <span className="text-[#092F7B] font-bold">
            {totalFeeCop.toLocaleString()} COP (1%)
          </span>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [activeAccount, setActiveAccount] = useState(0);

  const menus = [
    { title: 'Inicio', minWidth: '42px', ref: useRef(null), icon: (s) => <House className={s} /> },
    { title: 'Personas', minWidth: '67px', ref: useRef(null), icon: (s) => <CircleUserRound className={s} /> },
    { title: 'Empresas', minWidth: '71px', ref: useRef(null), icon: (s) => <Handshake className={s} /> },
    { title: 'Sobre Flowex Pay', ref: useRef(null), minWidth: '132px', icon: (s) => <Building2 className={s} /> },
    { title: 'FAQ', minWidth: '31px', ref: useRef(null), icon: (s) => <FileQuestion className={s} /> },
  ];

  const onSelectMenuBar = (idx) => {
    setActiveTab(idx);
    menus[Math.min(idx, menus.length - 1)].ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative w-full bg-white text-[#0A0D12]">
      <Header activeTab={activeTab} onSelectMenuBar={onSelectMenuBar} menus={menus} />

      {/* ==================== HERO (full-screen video, ARQ-style) ==================== */}
      <section ref={menus[0].ref} className="relative h-screen min-h-[700px] w-full overflow-hidden">
        {/* Background video */}
        <video
          src={HeroVideo}
          poster={ThumbImage1}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark gradient overlay for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />

        {/* Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <Reveal>
              <div className="max-w-3xl space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium text-white border border-white/20">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  Ya disponible en iOS y Android
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.02] tracking-tight text-white">
                  Finanzas globales,{' '}
                  <span className="text-[#6B9BFF]">sin fronteras.</span>
                </h1>
                <p className="text-lg md:text-2xl text-white/85 leading-relaxed max-w-2xl">
                  Envía dinero al mundo en minutos, recibe pagos en divisas locales y haz crecer tus dólares — todo desde una sola app.
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <button
                    onClick={() => navigate('/register')}
                    className="group flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-[#092F7B] font-semibold rounded-full transition-all hover:scale-105 cursor-pointer"
                  >
                    Abrir cuenta gratis
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-8 py-4 border-2 border-white/40 hover:border-white text-white font-semibold rounded-full transition-all backdrop-blur-md bg-white/5 cursor-pointer"
                  >
                    Iniciar sesión
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 pt-4">
                  {Apps.map((app, i) => (
                    <a
                      key={i}
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-5 py-3 bg-black/60 hover:bg-black/80 backdrop-blur-md text-white rounded-xl transition-all hover:scale-105 border border-white/10"
                    >
                      <img src={app.icon} alt={app.platform} className="w-7 h-7" />
                      <div className="text-left">
                        <p className="text-[10px] text-white/70">{app.description}</p>
                        <p className="text-sm font-semibold">{app.platform}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/80 rounded-full" />
          </div>
        </div>
      </section>

      {/* ==================== PARTNERS STRIP ==================== */}
      <section className="border-y border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <p className="text-center text-sm font-medium text-gray-500 mb-6 tracking-wide uppercase">
            Respaldado por líderes globales en tecnología financiera
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {Partners.map((p, i) => (
              <img
                key={i}
                src={p.image}
                alt={p.name}
                className="h-8 md:h-10 grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== CÓMO FUNCIONA (3 PASOS) ==================== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase mb-4">
                Cómo funciona
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Empezar toma{' '}
                <span className="text-[#092F7B]">menos de 5 minutos.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Sin papeleos, sin visitas a sucursales, sin sorpresas.
              </p>
            </div>
          </Reveal>

          <div className="relative grid md:grid-cols-3 gap-8 lg:gap-4">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-14 left-[16%] right-[16%] h-[2px] bg-gradient-to-r from-[#092F7B]/20 via-[#092F7B]/40 to-[#092F7B]/20" />

            {[
              {
                step: '01',
                icon: Download,
                title: 'Descarga la app',
                description: 'Gratis en App Store o Google Play. También puedes registrarte desde tu computador en flowexpay.com.',
              },
              {
                step: '02',
                icon: UserCheck,
                title: 'Verifica tu identidad',
                description: 'Un proceso seguro en 2 minutos con tu cédula y una selfie. Cumplimos con UIAF y KYC colombiano.',
              },
              {
                step: '03',
                icon: Send,
                title: 'Envía a todo el mundo',
                description: 'Recarga tu cuenta vía PSE y empieza a mover dinero a más de 50 países en minutos.',
              },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 150}>
                <div className="relative text-center">
                  <div className="relative z-10 inline-flex items-center justify-center w-28 h-28 bg-white rounded-full shadow-lg ring-8 ring-white border border-gray-100 mb-6">
                    <s.icon className="w-12 h-12 text-[#092F7B]" strokeWidth={1.5} />
                    <div className="absolute -top-2 -right-2 w-10 h-10 bg-[#092F7B] text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                      {s.step}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{s.title}</h3>
                  <p className="text-gray-600 leading-relaxed max-w-xs mx-auto">
                    {s.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FEATURE 1: TRANSFERENCIAS ==================== */}
      <section ref={menus[1].ref} className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#1056E1]/10 to-transparent rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden shadow-xl aspect-square bg-gray-100">
                <video
                  src={Video2}
                  poster={ThumbImage2}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="space-y-6 order-1 lg:order-2">
              <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase">
                01 — Transferencias globales
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Envía dinero al mundo en minutos,{' '}
                <span className="text-gray-400">no en días.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Conecta con más de 50 países sin intermediarios. Paga rentas, proveedores o familia con tasas hasta 6x mejores que tu banco.
              </p>
              <ul className="space-y-3">
                {[
                  'Llegada en minutos, no días',
                  'Tasas hasta 6x mejores que los bancos',
                  'Sin comisiones ocultas, todo transparente',
                ].map((t, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="group inline-flex items-center gap-2 px-8 py-4 bg-[#092F7B] hover:bg-[#0a3a96] text-white font-semibold rounded-full transition-all"
              >
                Enviar ahora
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ==================== FEATURE 2: INVERSIONES ==================== */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="space-y-6">
                <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase">
                  02 — Inversiones
                </p>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                  Acceso institucional a{' '}
                  <span className="text-gray-400">los mercados de Estados Unidos.</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Invierte en acciones, ETFs, índices y opciones de Estados Unidos desde tu app. Ejecución a través de un broker-dealer regulado en USA, análisis institucional con inteligencia artificial y un modelo de comisiones diseñado para que la inversión pequeña también sea rentable.
                </p>
                <ul className="space-y-3">
                  {[
                    { strong: 'Inversión mínima desde $12 USD', rest: ' — acciones fraccionarias accesibles desde el primer dólar.' },
                    { strong: 'Comisiones híbridas inteligentes', rest: ' — desde $0.50 por trade pequeño y máximo $30 en operaciones grandes. Sin sorpresas.' },
                    { strong: 'Acciones, ETFs, índices y opciones', rest: ' — todo el mercado de Estados Unidos, en español, desde Colombia.' },
                    { strong: 'Cuenta protegida por SIPC hasta $500,000', rest: ' — la misma cobertura que un cliente de Wall Street.' },
                    { strong: 'El Oráculo · tu mesa de análisis institucional', rest: ' — habla contigo cuando exploras una acción y te escribe cada mes para mostrarte cómo va tu plata y qué viene.' },
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700"><strong className="text-gray-900">{t.strong}</strong>{t.rest}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/register')}
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-[#092F7B] hover:bg-[#0a3a96] text-white font-semibold rounded-full transition-all"
                >
                  Empezar a invertir
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="space-y-4">
                {/* Card 1 — Portafolio compacto */}
                <div className="relative rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-[#092F7B] to-[#0a3a96] p-6 text-white">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/60 mb-1">Tu portafolio</p>
                        <p className="text-2xl font-bold">$12,847.50</p>
                        <p className="text-xs text-green-300 mt-0.5 flex items-center gap-1">
                          <span>↑</span> +$1,234.20 (+10.6%) este año
                        </p>
                      </div>
                      <div className="px-2.5 py-1 bg-green-500/20 text-green-300 text-[10px] font-semibold rounded-full border border-green-500/30">
                        SIPC $500K
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { ticker: 'AAPL', chg: '+1.8%', positive: true },
                        { ticker: 'VOO', chg: '+0.9%', positive: true },
                        { ticker: 'NVDA', chg: '+2.4%', positive: true },
                        { ticker: 'TSLA', chg: '-1.2%', positive: false },
                      ].map((h, i) => (
                        <div key={i} className="p-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 text-center">
                          <p className="text-[10px] font-bold">{h.ticker}</p>
                          <p className={`text-[10px] ${h.positive ? 'text-green-300' : 'text-red-300'}`}>{h.chg}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Card 2 — Modelo de comisiones */}
                <div className="rounded-2xl bg-gray-50 border border-gray-200 p-5">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-3">Comisiones híbridas · sin sorpresas</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { range: '$12 – $50', fee: '$0.50' },
                      { range: '$50 – $500', fee: '$2' },
                      { range: '$500 – $10K', fee: '0.3%' },
                      { range: '$10K +', fee: '$30 máx' },
                    ].map((c, i) => (
                      <div key={i} className="text-center">
                        <p className="text-[10px] text-gray-500 mb-0.5">{c.range}</p>
                        <p className="text-sm font-bold text-[#092F7B]">{c.fee}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ==================== EL ORÁCULO (DEEP-DIVE) ==================== */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#092F7B] via-[#0a3a96] to-[#092F7B] text-white overflow-hidden">
        {/* Background accents */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-yellow-400/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400/10 backdrop-blur-md rounded-full text-xs font-bold tracking-wider border border-yellow-400/20 mb-6">
                <Star className="w-3.5 h-3.5 text-yellow-300" />
                <span className="text-yellow-300">EL ORÁCULO</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Tu mesa de análisis institucional,{' '}
                <span className="text-yellow-300">en tu bolsillo.</span>
              </h2>
              <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-2xl mx-auto">
                El Oráculo habla cuando le preguntas, y vuelve cada mes — sin que se lo pidas — para mostrarte cómo va tu plata y qué viene.
              </p>
            </div>
          </Reveal>

          {/* Los 2 momentos */}
          <div className="grid lg:grid-cols-2 gap-8 mb-24">
            {/* Momento 1 — Reactivo */}
            <Reveal>
              <div className="h-full p-8 bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 hover:border-yellow-400/30 transition-all">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-yellow-300" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-yellow-300 font-bold">Momento 1 · Cuando preguntas</p>
                    <h3 className="text-xl font-bold mt-0.5">Tap en una acción → el Oráculo opina.</h3>
                  </div>
                </div>
                <p className="text-white/75 leading-relaxed mb-6">
                  Tocas cualquier acción y el Oráculo te explica qué es esa empresa, en qué momento está y qué riesgos tiene. Después, te muestra cómo encaja en <strong className="text-white">tu</strong> portafolio.
                </p>

                {/* Mini mockup acción */}
                <div className="bg-white/5 rounded-2xl border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white text-[#092F7B] rounded-lg flex items-center justify-center font-bold text-sm">
                        AAPL
                      </div>
                      <div>
                        <p className="font-bold text-sm">Apple Inc.</p>
                        <p className="text-xs text-white/60">$198.52 · <span className="text-green-300">+1.34%</span></p>
                      </div>
                    </div>
                    <div className="px-2 py-1 bg-yellow-400/10 text-yellow-300 text-[10px] font-bold rounded-full border border-yellow-400/20">
                      EL ORÁCULO
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4 space-y-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-yellow-300/80 font-semibold mb-1">Sobre la acción</p>
                      <p className="text-sm text-white/85 leading-relaxed">
                        AAPL cotiza a 31x P/E, históricamente caro pero justificable por el ciclo de servicios y el momentum de IA.
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-yellow-300/80 font-semibold mb-1">Sobre tu portafolio</p>
                      <p className="text-sm text-white/85 leading-relaxed">
                        Ya tienes 18% del portafolio en tecnología — si sumas, sales del rango balanceado de tu perfil.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Momento 2 — Proactivo */}
            <Reveal delay={150}>
              <div className="h-full p-8 bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 hover:border-yellow-400/30 transition-all">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-yellow-300" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-yellow-300 font-bold">Momento 2 · Cada mes</p>
                    <h3 className="text-xl font-bold mt-0.5">Una carta personal, sin que la pidas.</h3>
                  </div>
                </div>
                <p className="text-white/75 leading-relaxed mb-6">
                  El primer lunes del mes, recibes una carta del Oráculo con cómo va tu plata, qué pasó con tus acciones y qué viene — macro y por acción.
                </p>

                {/* Mini mockup carta */}
                <div className="bg-white/5 rounded-2xl border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-white/10">
                    <p className="text-[10px] uppercase tracking-wider text-yellow-300 font-bold">Carta mensual · Mayo 2026</p>
                    <p className="text-[10px] text-white/60">3 min</p>
                  </div>

                  <div className="space-y-3">
                    <div className="border-l-2 border-yellow-400 pl-3">
                      <p className="text-[10px] uppercase tracking-wider text-white/60 mb-0.5">Cómo va tu plata</p>
                      <p className="text-sm text-white/90">+10.6% año · superando al S&P 500 por 2.3 puntos</p>
                    </div>
                    <div className="border-l-2 border-white/30 pl-3">
                      <p className="text-[10px] uppercase tracking-wider text-white/60 mb-0.5">Tus acciones</p>
                      <p className="text-sm text-white/90">Earnings de AAPL y NVDA esta semana. Rotación parcial de NVDA → defensivos.</p>
                    </div>
                    <div className="border-l-2 border-white/30 pl-3">
                      <p className="text-[10px] uppercase tracking-wider text-white/60 mb-0.5">Qué viene</p>
                      <p className="text-sm text-white/90">Decisión Fed el 18 · earnings Q2 · ajuste de tesis tech defensiva.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* 4 Tiers */}
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-wider text-yellow-300 font-bold mb-3">4 NIVELES DE PROFUNDIDAD</p>
              <h3 className="text-2xl md:text-4xl font-bold leading-tight">
                A más patrimonio invertido,{' '}
                <span className="text-yellow-300">más profundidad de análisis.</span>
              </h3>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                emoji: '🌱',
                name: 'Básico',
                tier: 'Tier 1',
                range: '$12 – $250 USD',
                features: ['Análisis simple por acción', 'Carta mensual corta', 'Datos clave del portafolio'],
              },
              {
                emoji: '🌿',
                name: 'Estándar',
                tier: 'Tier 2',
                range: '$250 – $2K USD',
                features: ['Análisis con impacto en portafolio', 'Carta mensual completa', 'Macro básico'],
              },
              {
                emoji: '🌳',
                name: 'Premium',
                tier: 'Tier 3',
                range: '$2K – $10K USD',
                features: ['Análisis completo con tesis', 'Comparaciones históricas', 'Macro + visión por sectores'],
              },
              {
                emoji: '🏛️',
                name: 'Institucional',
                tier: 'Tier 4',
                range: '$10K+ USD',
                features: ['Todo lo anterior', 'Alertas tácticas en tiempo real', 'Análisis personalizado'],
              },
            ].map((tier, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="h-full p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:border-yellow-400/30 hover:bg-white/10 transition-all">
                  <div className="text-3xl mb-4">{tier.emoji}</div>
                  <p className="text-[10px] uppercase tracking-wider text-yellow-300 font-bold mb-1">{tier.tier}</p>
                  <h4 className="text-lg font-bold mb-1">{tier.name}</h4>
                  <p className="text-sm text-white/60 mb-4">{tier.range}</p>
                  <ul className="space-y-2">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/85">
                        <Check className="w-4 h-4 text-yellow-300 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={400}>
            <p className="text-center text-sm text-white/50 mt-10 max-w-2xl mx-auto">
              El Oráculo no recomienda comprar o vender. Te da contexto, análisis y educación financiera para que tomes mejores decisiones. La inversión implica riesgo.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ==================== FEATURE 3: CUENTAS VIRTUALES (TABS) ==================== */}
      <section ref={menus[2].ref} className="py-24 lg:py-32 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase mb-4">
                03 — Cuentas virtuales
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Una cuenta en cada país,{' '}
                <span className="text-gray-400">sin salir de casa.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Recibe pagos de clientes globales como si tuvieras una cuenta bancaria local. USD, EUR, MXN, BRL.
              </p>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="relative rounded-[2rem] overflow-hidden shadow-xl aspect-[4/5] bg-gray-100 max-w-md mx-auto w-full">
                <video
                  key={activeAccount}
                  src={VirtualAccounts[activeAccount].video}
                  poster={VirtualAccounts[activeAccount].thumb}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-3">
                {VirtualAccounts.map((acc, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveAccount(i)}
                    className={`w-full flex items-center gap-4 p-5 rounded-2xl text-left transition-all cursor-pointer border-2 ${
                      activeAccount === i
                        ? 'bg-white border-[#092F7B] shadow-lg'
                        : 'bg-white/50 border-transparent hover:bg-white hover:border-gray-200'
                    }`}
                  >
                    <img src={acc.flag} alt={acc.country} className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{acc.country}</p>
                      <p className="text-sm text-gray-500">Cuenta en {acc.currency}</p>
                    </div>
                    <ArrowRight
                      className={`w-5 h-5 transition-all ${
                        activeAccount === i ? 'text-[#092F7B] translate-x-0' : 'text-gray-300 -translate-x-2'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ==================== FEATURE 4: TASAS (CALCULATOR) ==================== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="space-y-6">
              <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase">
                04 — Tasas transparentes
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Las tasas que tu banco{' '}
                <span className="text-gray-400">preferiría que no vieras.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Sin márgenes inflados. Sin comisiones ocultas. Pruébalo tú mismo en tiempo real.
              </p>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <CurrencyCalculator />
          </Reveal>
        </div>
      </section>

      {/* ==================== COMPARADOR BANCO VS FLOWEXPAY ==================== */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase mb-4">
                La diferencia
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Tu banco vs.{' '}
                <span className="text-[#092F7B]">FlowexPay.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Misma operación. Resultado completamente distinto.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Banco tradicional */}
            <Reveal>
              <div className="h-full p-8 md:p-10 bg-white rounded-3xl border border-gray-200 relative">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-500">Banco tradicional</h3>
                </div>
                <ul className="space-y-5">
                  {[
                    { icon: Clock, label: 'Tiempo de envío', value: '3 a 5 días hábiles' },
                    { icon: DollarSign, label: 'Comisión promedio', value: '5% – 8% del monto' },
                    { icon: FileText, label: 'Papeleo', value: 'Formularios, copias, filas' },
                    { icon: Smartphone, label: 'Experiencia', value: 'Llamadas y visitas' },
                    { icon: Globe2, label: 'Países', value: 'Limitado, con corresponsales' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                        <X className="w-5 h-5 text-red-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">{item.label}</p>
                        <p className="font-semibold text-gray-700">{item.value}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* FlowexPay */}
            <Reveal delay={150}>
              <div className="h-full p-8 md:p-10 bg-[#092F7B] rounded-3xl text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />

                <div className="relative">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold">FlowexPay</h3>
                    <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-300 text-xs font-semibold rounded-full border border-green-500/30">
                      Recomendado
                    </span>
                  </div>
                  <ul className="space-y-5">
                    {[
                      { label: 'Tiempo de envío', value: 'Minutos, no días' },
                      { label: 'Comisión', value: '0,5% entrada + 0,5% salida' },
                      { label: 'Papeleo', value: 'Solo cédula y selfie' },
                      { label: 'Experiencia', value: '100% desde tu celular' },
                      { label: 'Países', value: '+50 países directo' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-4">
                        <div className="w-9 h-9 bg-green-500/20 rounded-lg flex items-center justify-center shrink-0 backdrop-blur-sm">
                          <Check className="w-5 h-5 text-green-300" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-white/60">{item.label}</p>
                          <p className="font-semibold">{item.value}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES GRID ==================== */}
      <section ref={menus[3].ref} className="py-24 lg:py-32 bg-[#092F7B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="max-w-3xl mb-16">
              <p className="text-sm font-semibold text-white/60 tracking-wide uppercase mb-4">
                Por qué FlowexPay
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Construido para moverse a la velocidad del mundo.
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {Features.map((f, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="h-full p-8 bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                    <f.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{f.title}</h3>
                  <p className="text-white/70 leading-relaxed">{f.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SEGURIDAD EXPANDIDA ==================== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-sm font-semibold text-[#092F7B] tracking-wide uppercase mb-4">
                Seguridad institucional
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Tu dinero, resguardado por los{' '}
                <span className="text-[#092F7B]">mismos que protegen a los bancos.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Nos apoyamos en infraestructura, cumplimiento y custodia de nivel institucional.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Lock,
                partner: FireblocksIcon,
                title: 'Custodia institucional',
                description:
                  'Tus activos están custodiados por Fireblocks, la plataforma usada por BNY Mellon, BNP Paribas y más de 1.800 instituciones financieras globales.',
                badge: '1.800+ instituciones globales',
              },
              {
                icon: FileCheck,
                partner: BridgeIcon,
                title: 'Cumplimiento regulado',
                description:
                  'Operamos con Bridge, plataforma regulada en EE.UU. (respaldada por Stripe), más licencias locales y registro UIAF/KYC en Colombia.',
                badge: 'UIAF + KYC Colombia',
              },
              {
                icon: Server,
                partner: AwsIcon,
                title: 'Infraestructura bancaria',
                description:
                  'Nuestra plataforma corre sobre Amazon Web Services, con cifrado end-to-end, alta disponibilidad y los mismos estándares que los grandes bancos.',
                badge: '99.9% uptime',
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="h-full p-8 bg-white rounded-3xl border border-gray-100 hover:border-[#092F7B]/20 hover:shadow-xl transition-all group">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-[#092F7B]/5 rounded-2xl flex items-center justify-center group-hover:bg-[#092F7B]/10 transition-colors">
                      <item.icon className="w-7 h-7 text-[#092F7B]" />
                    </div>
                    <img src={item.partner} alt="partner" className="h-7 opacity-70 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{item.description}</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 text-xs font-semibold rounded-full">
                    <Check className="w-3.5 h-3.5" />
                    {item.badge}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={300}>
            <div className="max-w-4xl mx-auto text-center pt-12 border-t border-gray-100">
              <p className="text-gray-500 mb-4 text-sm">
                FlowexPay es operado por <span className="font-semibold text-gray-700">Loaded Colombia SAS</span>, empresa registrada en Colombia.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
                {['Registro UIAF', 'Cumplimiento KYC', 'AES-256 Encryption'].map((b, i) => (
                  <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 font-medium rounded-full">
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Lo que dicen quienes ya{' '}
                <span className="text-gray-400">no vuelven atrás.</span>
              </h2>
              <div className="mt-6 flex items-center justify-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-600 font-medium">4.8/5 en App Store y Google Play</span>
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {Reviews.map((r, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="h-full p-8 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all">
                  <div className="flex gap-0.5 mb-6">
                    {[...Array(r.rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-8">"{r.text}"</p>
                  <div className="flex items-center gap-3 pt-6 border-t border-gray-100">
                    <img src={r.avatar} alt={r.username} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <p className="font-semibold">{r.username}</p>
                      <p className="text-sm text-gray-500">{r.location}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== DOWNLOAD APP CTA ==================== */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-gradient-to-br from-[#092F7B] via-[#0a3a96] to-[#1056E1]">
        <video
          src={Video5}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center text-white">
          <Reveal>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
              Lleva FlowexPay contigo,{' '}
              <span className="text-white/60">donde vayas.</span>
            </h2>
            <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              Descarga la app y empieza a mover tu dinero sin fronteras en menos de 5 minutos.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              {Apps.map((app, i) => (
                <a
                  key={i}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 px-7 py-4 bg-white hover:bg-gray-50 text-[#0A0D12] rounded-2xl transition-all hover:scale-105"
                >
                  <img src={app.icon} alt={app.platform} className="w-9 h-9" />
                  <div className="text-left">
                    <p className="text-xs text-gray-500">{app.description}</p>
                    <p className="text-base font-bold">{app.platform}</p>
                  </div>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section ref={menus[4].ref} className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                Preguntas frecuentes
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Todo lo que necesitas saber antes de empezar.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="space-y-3">
              {Faqs.map((faq, i) => (
                <FaqItem key={i} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      <WhatsApp />

      {/* Inline keyframes for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
      `}</style>
    </div>
  );
}
